import type { ChangeEvent } from 'react'
import React from 'react'

import { FormControl, InputLabel, MenuItem, Select } from '@material-ui/core'
import { withStyles } from '@material-ui/core/styles'
import { batch as reduxBatch, connect } from 'react-redux'

import type { ClassNameMap } from '@material-ui/core/styles/withStyles'
import type { AnyAction } from '@reduxjs/toolkit'
import type { ThunkDispatch } from 'redux-thunk'

import { HistoryUpdate } from 'api/history'
import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import { getMap } from 'utils/MapUtils'
import PoziNotifications from 'utils/notifications'
import { layerValidInCurrentProfile } from 'utils/profile'

import { setCurrentProfile, setLayerUnselected } from '../actions'

const styles = {
  root: {},
  formControl: {},
  profile: {
    margin: 16,
    marginBottom: 10,
    fontWeight: 'bold'
  },
  // Give a bit of extra spacing around an item with a divider
  menuItem: {
    '&&.MuiListItem-divider': {
      paddingBottom: 10,
      marginBottom: 10
    }
  },
  select: {}
}

class Profile_ extends React.Component<ProfileProps, ProfileState> {
  componentDidMount () {
    // console.debug('Profile - componentDidMount', this.props)

    const { dispatchSetCurrentProfile, profiles } = this.props

    // Get current profile from the model (i.e. from the URL params, e.g. 'profile[assets]')
    const profileFromModel: string = Model.getState('profile', null)

    // If a profile named 'all' or '*' was used in the URL, behave as if no profile was set
    if (profileFromModel === 'all' || profileFromModel === '*') {
      console.debug(`Profile '${profileFromModel}' was used in URL. Quietly ignoring and just showing all layers.`)

      // Remove this profile from URL
      this.updateProfile('')

      return
    }

    if (profileFromModel) {
      const profileFromModelIsValid = profileFromModel in profiles
      if (profileFromModelIsValid) {
      // Store current profile in redux
        dispatchSetCurrentProfile(profileFromModel)
      } else {
        console.log('Profile is not found in any layers', profileFromModel, profiles)

        PoziNotifications().warning(`Profile '${profileFromModel}' cannot be not found. Showing all layers.`)

        // Remove invalid profile from URL
        this.updateProfile('')
      }
    }
  }

  componentDidUpdate (prevProps: ProfileProps) {
    // console.debug('Profile - componentDidUpdate')

    const { currentProfile: prevCurrentProfile } = prevProps
    const { currentProfile: nextCurrentProfile, profiles } = this.props

    const profileChanged = prevCurrentProfile !== nextCurrentProfile

    // console.debug('Profile - profile changed?', profileChanged, prevCurrentProfile, nextCurrentProfile)

    // Find layers that need to be hidden due to profile change. If the next profile
    // is empty (i.e. showing all available layers), then no layers need to be hidden
    if (profileChanged && nextCurrentProfile) {
      // console.debug('Profile - profile changed!', prevCurrentProfile, nextCurrentProfile)

      // The layers to hide will be the ones that were in the previous profile but not in the next
      const visibleLayers = this.getVisibleLayersFromMap()
      const layersToHide = visibleLayers.filter((titleid: string) => !layerValidInCurrentProfile(titleid))

      console.debug('Profile - profile changed - layers to hide', layersToHide)

      // Actually hide the relevant layers
      this.hideLayers(layersToHide)
    }
  }

  // Method to get all visible layers that have a profile
  getVisibleLayersFromMap = () => {
    const map = getMap()

    const visibleLayers = map
      .getAllLayers()
      .filter(olLayer => olLayer.getVisible())
      .map(olLayer => olLayer.get('titleid') as string)

    // console.debug('Profile - getVisibleLayersFromMap - visible layers', visibleLayers)

    return visibleLayers
  }

  // Method to hide layers from map and layer tree. Catalogue layers will be igored because
  // they themselves are not shown on the map but their children are.
  hideLayers = (layers: string[]) => {
    console.debug('Profile - hideLayers', layers)

    const { dispatchSetLayerUnselected } = this.props

    // Create list of layers to hide
    const layersToHide: string[] = []

    for (const titleid of layers) {
      const layerConf = PoziUtils.getLayerFromTitleID(titleid)
      const layerIsCatalogue = layerConf && PoziUtils.isLayerTypeCatalogue(layerConf)
      if (layerIsCatalogue) {
        // console.debug('Profile - hideLayers - layer is catalogue. Ignoring.', titleid)

        continue
      }

      const olLayer = PoziUtils.getOpenLayersObjectFromTitleID(titleid)

      if (!olLayer) {
        console.log('Profile - hideLayers - layer not found', titleid, '. Ignoring.')
        continue
      }

      const layerIsVisible = olLayer.getVisible()
      if (!layerIsVisible) {
        // console.debug('Profile - hideLayers - layer not visible', titleid)

        continue
      }

      layersToHide.push(titleid)
    }

    // console.debug('Profile - hideLayers - layers to hide', layersToHide)

    // Tell redux to unselect the layers and do it in one batch to make these calls
    // more efficient
    reduxBatch(() => {
      layersToHide.forEach(titleid => {
        // console.debug('Profile - hideLayers - hiding layer', titleid)

        dispatchSetLayerUnselected(titleid)
      })
    })
  }

  onSelectChange = (evt: ChangeEvent<any>) => {
    const selectedProfile = evt.target?.value || ''

    console.debug('Profile - onSelectChange', selectedProfile, evt)

    this.updateProfile(selectedProfile)
  }

  updateProfile = (profile: string) => {
    const { dispatchSetCurrentProfile } = this.props

    // Inform redux about the new current profile
    dispatchSetCurrentProfile(profile)

    // Inform the model about the new current profile
    Model.setState('profile', null, profile)

    // Update the URL to reflect the new current profile
    HistoryUpdate()
  }

  render () {
    const { classes, currentProfile, profiles } = this.props

    // console.debug('Profile - render', currentProfile, profiles)

    const hasProfiles =
      profiles &&
      Object.keys(profiles).length > 0 &&
      !Object.keys(profiles).every(id => ['*', 'all'].includes(id)) // ignore if the only available profiles are '*' or 'all' or both

    if (!hasProfiles) {
      console.debug('Profile - render - no profiles', currentProfile, profiles)
      return null
    }

    const profileName = currentProfile ? profiles[currentProfile]?.name : 'None'

    // Make sure that the profiles appear in alphabetical order
    const profilesArrSorted = Object.values(profiles)
      .sort((a, b) => a.name < b.name ? -1 : 1) // sort alphabetically

    const menuItemArr = [
      <MenuItem className={classes.menuItem} key={0} value={''} divider={true}>
        All
      </MenuItem>
    ]

    // Generate menu items from profiles
    for (const profile of profilesArrSorted) {
      const { id, name } = profile

      // Ignore the wildcard profile
      if (id === '*') {
        continue
      }

      menuItemArr.push(
      <MenuItem className={classes.menuItem} key={id} value={id}>
        {name}
      </MenuItem>
      )
    }

    return (
      <div className={classes.profile}>
        <FormControl className={classes.formControl} fullWidth>
        <InputLabel
            shrink
          >
          Profile
        </InputLabel>
        <Select
          className={classes.select}
          value={currentProfile || ''}
          onChange={this.onSelectChange}
          displayEmpty
        >
          {menuItemArr}
        </Select>
      </FormControl>
      </div>
    )
  }
}

function mapStateToProps (state: ReduxState, _ownProps: ProfileProps) {
  const { currentProfile, profiles } = state

  return {
    currentProfile,
    profiles
  }
}

const mapDispatchToProps = (dispatch: ThunkDispatch<ReduxState, undefined, AnyAction>) => {
  return {
    dispatchSetLayerUnselected: (titleid: string) => {
      dispatch(setLayerUnselected(titleid))
    },
    dispatchSetCurrentProfile: (profileId: string) => {
      dispatch(setCurrentProfile(profileId))
    }
  }
}

const Profile = (
  // @ts-expect-error
  withStyles(styles)(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(Profile_)
  )
)

export default Profile

interface ProfileProps extends ReduxState{
  classes: Partial<ClassNameMap<keyof typeof styles>>
  dispatchSetLayerUnselected: (titleid: string) => void
  dispatchSetCurrentProfile: (profileId: string) => void
}

interface ProfileState {}

interface ReduxState {
  currentProfile?: string
  profiles: Record<string, any>
}
